// Pure helpers for the Shipment tracking screens (spec: shipment-tracking).
//
// Everything that decides a word, a colour, an enabled button or a request payload lives here, so
// it can be proved with the bare jest that ships with react-scripts. The components render these
// answers and nothing more. No JSX, no antd and no `src/config` import belongs in this file — the
// tests must be able to load it without a browser or a store.
//
// The customer-facing copy below is word for word the w-api §15.6 text (verified against
// `services/tracking/gh.py`). When the server sends a title we show the server's; these strings are
// only the fallback, and they must stay identical to the words the customer's app is showing.

// --- Vocabulary -------------------------------------------------------------------------------

export const STAGE_ORDER = [
  'waiting',
  'booked',
  'loaded',
  'at_sea',
  'arrived',
  'discharged',
  'collected'
]

// Customer copy, word for word from §15.6. The placeholders stay as written: the SERVER fills them.
export const STAGE_COPY = {
  waiting: {
    title: 'Waiting for the shipping line',
    explanation:
      'We\'ve asked {line} for your shipment. The first update can take up to a day.'
  },
  booked: {
    title: 'Booked',
    explanation: 'Space on the ship is reserved. Nothing has been loaded yet.'
  },
  loaded: {
    title: 'Loaded at {pol}',
    explanation: 'Your cargo is on board and waiting to sail.'
  },
  at_sea: {
    title: 'At sea',
    explanation: 'On its way to {pod}. Ships sometimes stop at other ports first.'
  },
  arrived: {
    title: 'Arrived at {pod}',
    explanation: 'The ship is in port. Unloading comes next.'
  },
  discharged: {
    title: 'Unloaded at {pod}',
    explanation: 'Your cargo is off the ship. Customs clearing comes next.'
  },
  collected: {
    title: 'Collected from the port',
    explanation: 'It has left the port. Tracking is complete.'
  }
}

// §15.6: status `completed` WITHOUT a gate-out. The stage stays where it was; this is what the
// customer reads instead of a stage title. Never call it "collected".
export const ENDED_COPY = {
  title: 'Tracking has ended',
  explanation:
    '{line} stopped sending updates after your cargo was unloaded at {pod}. The last update we received is below.'
}

// The server's `completion` field ("collected" | "ended" | null) decides this — never infer it from
// the stage, which keeps its own title even after tracking has ended (§15.5 ruling).
export const isEnded = completion => completion === 'ended'

// The API's title wins; our fallback drops the port placeholder rather than printing it.
export const stageTitle = (key, serverTitle) => {
  if (serverTitle && String(serverTitle).trim()) return serverTitle
  const copy = STAGE_COPY[key]
  if (copy) return copy.title.replace(/ at \{(?:pol|pod)\}/, '')
  return key ? String(key) : ''
}

// The app's five tones (w-app `lib/garage/status.ts` StatusTone) → antd Tag colours. One place.
export const TONE_COLORS = {
  success: 'green',
  warning: 'gold',
  danger: 'red',
  info: 'blue',
  neutral: 'default'
}

export const STAGE_TONES = {
  waiting: 'neutral',
  booked: 'info',
  loaded: 'info',
  at_sea: 'info',
  arrived: 'info',
  discharged: 'success',
  collected: 'success'
}

// The shared app/dashboard colour table ruled in §15.5. An open alert paints the stage gold (the
// app shows the attention title there); an ended shipment stays grey, so a stage of `discharged`
// never reads as a real collection.
export const stageColor = (key, attentionKind, completion) => {
  if (attentionKind) return TONE_COLORS.warning
  if (completion === 'ended') return TONE_COLORS.neutral
  if (completion === 'collected') return TONE_COLORS.success
  return TONE_COLORS[STAGE_TONES[key]] || TONE_COLORS.neutral
}

// Team vocabulary — never customer copy.
export const STATUS_LABELS = {
  draft: 'Draft (unpaid)',
  awaiting_payment: 'Awaiting payment',
  starting: 'Starting',
  tracking: 'Tracking',
  needs_attention: 'Needs attention',
  completed: 'Completed',
  closed: 'Closed'
}

export const STATUS_COLORS = {
  draft: 'default',
  awaiting_payment: 'gold',
  starting: 'cyan',
  tracking: 'blue',
  // Attention is gold, never red (the shared colour ruling). Only the priority tag uses red.
  needs_attention: 'gold',
  completed: 'green',
  closed: 'default'
}

export const statusLabel = (status, completion) => {
  if (status === 'completed') {
    if (completion === 'collected') return 'Completed · collected'
    if (completion === 'ended') return 'Completed · ended without gate-out'
    // The server should always send one of the two; show the plain word rather than guess.
    return 'Completed'
  }
  return STATUS_LABELS[status] || status || '—'
}

export const statusColor = (status, completion) => {
  if (completion === 'collected') return 'green'
  if (completion === 'ended') return 'default'
  return STATUS_COLORS[status] || 'default'
}

export const ATTENTION = {
  start_failed: {
    label: 'ShipsGo did not start it',
    priority: 'urgent',
    customerTitle: 'We\'re setting this up',
    customerMessage:
      'Your payment is safe. Our team has been notified and is starting your tracking.'
  },
  not_found: {
    label: 'Not found by ShipsGo',
    priority: 'urgent',
    customerTitle: 'We\'re looking into this',
    customerMessage:
      'We haven\'t received anything from {line} for this number yet. Our team has been notified and will contact you.'
  },
  no_first_event: {
    label: 'No first update',
    priority: 'high',
    customerTitle: 'We\'re looking into this',
    customerMessage:
      'We haven\'t received anything from {line} for this number yet. Our team has been notified and will contact you.'
  },
  paused: {
    label: 'Updates paused',
    priority: 'low',
    customerTitle: 'Updates have paused',
    customerMessage:
      '{line} hasn\'t sent anything new for a while. We\'re checking and will update you.'
  }
}

// The ONLY place red appears for attention: the internal urgency tag. Every status, stage and
// banner that represents attention is gold.
export const PRIORITY_COLORS = { urgent: 'red', high: 'volcano', low: 'gold' }

export const attentionLabel = kind => (ATTENTION[kind] || {}).label || kind || ''

export const attentionPriority = (kind, serverPriority) =>
  serverPriority || (ATTENTION[kind] || {}).priority || 'low'

// What to actually do about it, for the box at the top of the detail page (Req 5.2).
const ATTENTION_GUIDANCE = {
  start_failed:
    'ShipsGo refused to create this shipment — usually no credits left, or ShipsGo was down. The customer has paid. Check the ShipsGo account page, buy credits if needed, then Re-track with the same number and line.',
  no_first_event:
    'Paid, but nothing has come from the line yet. Contact the customer to check the number and the line against their document. If either was wrong, Re-track. If both are right, Resolve with a note and give it more time.',
  not_found:
    'ShipsGo gave up on this number without ever finding it. It is almost always a wrong number or the wrong line. Contact the customer; Re-track with the corrected details, or, if it cannot be tracked, refund in Paystack, Record refund, then Close.',
  paused:
    'Updates came in, then stopped. It can be normal on long transhipments. Check the last event below; Resolve with a note once you are satisfied, or contact the customer if it has been days.'
}

export const attentionGuidance = kind =>
  ATTENTION_GUIDANCE[kind] || 'This shipment has an open alert. Check the history below.'

export const KIND_LABELS = { container: 'Container', reference: 'BL / booking' }

export const ACTION_LABELS = {
  retrack: 'Re-tracked',
  resolve: 'Resolved',
  close: 'Closed',
  note: 'Note',
  manual_refund: 'Refund recorded',
  credit_refund_requested: 'ShipsGo credit refund requested'
}

// --- Formatting -------------------------------------------------------------------------------
//
// Everything is shown in Ghana time. The team reads these out to customers standing in Tema, and a
// browser in another timezone must not shift a vessel's arrival by a day.

const ACCRA = 'Africa/Accra'

const parsed = iso => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: ACCRA,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
})

const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: ACCRA,
  day: '2-digit',
  month: 'short',
  year: 'numeric'
})

export const formatDateTime = iso => {
  const date = parsed(iso)
  return date ? DATE_TIME_FORMAT.format(date) : '—'
}

export const formatDate = iso => {
  const date = parsed(iso)
  return date ? DATE_FORMAT.format(date) : '—'
}

const MINUTE = 60000
const HOUR = 60 * MINUTE
const TWO_DAYS = 48 * HOUR

// How long ago, in the shortest form that is still honest. A future timestamp means the server and
// this browser disagree about the clock, which is not something to show as a negative age.
export const formatAge = (iso, nowMs = Date.now()) => {
  const date = parsed(iso)
  if (!date) return '—'
  const elapsed = nowMs - date.getTime()
  if (elapsed < MINUTE) return '<1m'
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`
  if (elapsed < TWO_DAYS) return `${Math.floor(elapsed / HOUR)}h`
  return `${Math.floor(elapsed / (24 * HOUR))}d`
}

export const money = (amount, currency = 'GHS') => {
  if (amount === null || amount === undefined || amount === '') return '—'
  return `${currency || 'GHS'} ${Number(amount).toFixed(2)}`
}

// Money crosses the wire as a two-decimal string, never a float.
export const toMoneyString = value => {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  return Number.isNaN(number) ? '' : number.toFixed(2)
}

// --- The worklist query -----------------------------------------------------------------------
//
// Query parameters are snake_case: the w-api parser camelizes bodies, not query strings.

export const buildShipmentQuery = ({
  view,
  status,
  attention,
  line,
  channel,
  updatesStopped,
  range,
  search
} = {}) => {
  const params = new URLSearchParams()
  // The Needs attention view is "anything with an open alert" unless a kind was picked, in which
  // case the kind is the narrower answer to the same question.
  if (attention) params.append('attention', attention)
  else if (view === 'attention') params.append('attention', 'any')
  if (status) params.append('status', status)
  if (line) params.append('line', line)
  // Snake_case, like every other filter here: the API's query parameters are snake_case
  // throughout (`created_after`), and only its JSON bodies are camelized.
  if (channel) params.append('channel', channel)
  if (updatesStopped) params.append('updates_stopped', updatesStopped)
  if (range && range[0]) params.append('created_after', range[0].toISOString())
  if (range && range[1]) params.append('created_before', range[1].toISOString())
  if (search && String(search).trim()) params.append('search', String(search).trim())
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// --- Where a shipment was started, and whether we are messaging about it ----------------------
//
// `channel` was `app` for everything until 2026-10-18, the website included, which made "how is
// the web flow doing" a question nobody could answer. It now also decides which flow a shipment
// came through: a WhatsApp one came from a bill of lading somebody sent us.

export const CHANNEL_LABELS = {
  app: 'App',
  web: 'Website',
  whatsapp: 'WhatsApp'
}

export const channelLabel = channel => CHANNEL_LABELS[channel] || channel || '—'

// Blue for the app, which is most of them; the other two stand out because they are the question
// being asked. No red anywhere: a channel is never a problem.
export const CHANNEL_COLORS = {
  app: 'blue',
  web: 'geekblue',
  whatsapp: 'green'
}

export const channelColor = channel => CHANNEL_COLORS[channel] || 'default'

// What the updates switch says, and why. The customer saying STOP and a staff member switching it
// off look identical on the row, which is why the date is shown: "they asked us to stop" and "it
// was never on" are different facts, and only one of them is a complaint.
export const updatesSummary = (shipment = {}) => {
  if (shipment.whatsappUpdates) return { on: true, label: 'Updates on', color: 'green' }
  return {
    on: false,
    label: shipment.updatesStoppedAt
      ? `Updates off · ${formatDate(shipment.updatesStoppedAt)}`
      : 'Updates off',
    color: 'default'
  }
}

// --- What a customer was told, and what we could not tell them --------------------------------

export const NOTIFY_LABELS = {
  found: 'Shipment found',
  stage: 'Reached a stage',
  eta: 'Arrival date moved',
  attention: 'Needs attention',
  ended: 'Tracking ended',
  intro: 'Why you\'re getting these'
}

export const notifyLabel = row => NOTIFY_LABELS[row && row.kind] || (row && row.kind) || '—'

export const NOTIFY_STATUS_COLORS = {
  sent: 'green',
  queued: 'blue',
  skipped: 'default',
  failed: 'red'
}

// Said in a sentence, from the code the server stored. The codes are short and specific, and a
// staff member reading "Outside the window — the template isn't approved yet" learns what to do
// about it, which a polished "could not be sent" does not.
export const NOTIFY_REASONS = {
  no_template: 'Outside the 24-hour window and the WhatsApp template isn\'t set up yet.',
  no_number: 'We have no number we can dial for this shipment.',
  no_copy: 'There is no customer wording for this update.',
  'updates off': 'The customer had asked us to stop before this one went out.'
}

export const notifyReason = row => {
  const reason = String((row && row.reason) || '').trim()
  if (!reason) return ''
  return NOTIFY_REASONS[reason] || reason
}

// --- Numbers ----------------------------------------------------------------------------------
//
// Mirrors w-api §15.2 so the re-track dialog refuses exactly what the server would, before a
// credit is at stake.

export const normalizeNumber = raw =>
  String(raw || '')
    .replace(/\s+/g, '')
    .toUpperCase()

export const CONTAINER_RE = /^[A-Z]{3}[UJZ][0-9]{7}$/
export const REFERENCE_RE = /^[A-Z0-9/-]{4,40}$/

// ISO 6346: letters are valued from A=10 upward, skipping every multiple of 11.
const LETTER_VALUES = (() => {
  const values = {}
  let value = 10
  for (let i = 0; i < 26; i += 1) {
    if (value % 11 === 0) value += 1
    values[String.fromCharCode(65 + i)] = value
    value += 1
  }
  return values
})()

export const iso6346Valid = n => {
  const number = String(n || '')
  if (!CONTAINER_RE.test(number)) return false
  let sum = 0
  for (let i = 0; i < 10; i += 1) {
    const char = number[i]
    const value = i < 4 ? LETTER_VALUES[char] : Number(char)
    sum += value * Math.pow(2, i)
  }
  return ((sum % 11) % 10) === Number(number[10])
}

// A container-shaped string is never read as a reference: sending a mistyped container number to
// ShipsGo as a booking number is how a shipment quietly never starts.
export const classifyNumber = n => {
  const number = String(n || '')
  if (CONTAINER_RE.test(number)) {
    return iso6346Valid(number) ? 'container' : 'check_digit_failed'
  }
  if (REFERENCE_RE.test(number)) return 'reference'
  return 'invalid'
}

export const lineTracks = (line, kind) => {
  if (!line) return false
  if (kind === 'container') return !!line.tracksContainer
  if (kind === 'reference') return !!line.tracksReference
  return false
}

// --- Finishing a bill of lading by hand (spec: whatsapp-shipment-tracking Req 1.2) -----------
//
// A staff member is doing with their eyes what the reader could not: reading the line and the
// number off the page. The same two rules the customer's own path applies are applied here BEFORE
// the request, so a mistyped number is caught on the screen in front of them rather than coming
// back as a refusal — the server checks again, and it also spends the credit.

export const validateBillOfLading = ({ number, lineId, lines } = {}) => {
  const errors = []
  const normalized = normalizeNumber(number)
  const kind = classifyNumber(normalized)

  if (!normalized) errors.push('Type the number printed on the bill of lading.')
  else if (kind === 'check_digit_failed')
    errors.push(
      'That container number fails its check digit — read it again, character by character.'
    )
  else if (kind === 'invalid')
    errors.push('That is not a container, bill of lading or booking number.')

  if (!lineId) errors.push('Choose the shipping line printed on the document.')
  else if (kind === 'container' || kind === 'reference') {
    const line = (lines || []).find(row => String(row.id) === String(lineId))
    if (line && !lineTracks(line, kind)) {
      errors.push(
        kind === 'container'
          ? `${line.name} is tracked by bill of lading, not by container number.`
          : `${line.name} is tracked by container number, not by bill of lading.`
      )
    }
  }
  return errors
}

// Containers are sent as a list because a bill of lading can carry several, and each is checked
// by the server the same way the reader's are. A blank entry is not a container.
//
// Split on commas, semicolons and newlines — NOT on spaces. A container number is PRINTED on the
// box in groups ("MSKU 123456 5"), which is how a person reading one off a document types it, and
// splitting on whitespace turned that into three fragments. `normalizeNumber` removes the spaces
// inside each entry, so both spellings arrive as the same string the app sends.
export const buildBillOfLadingPayload = (form = {}) => ({
  number: normalizeNumber(form.number),
  lineId: form.lineId,
  vessel: String(form.vessel || '').trim(),
  voyage: String(form.voyage || '').trim(),
  pol: String(form.pol || '').trim(),
  pod: String(form.pod || '').trim(),
  containers: String(form.containers || '')
    .split(/[,;\r\n]+/)
    .map(value => normalizeNumber(value))
    .filter(Boolean)
})

// --- Actions ----------------------------------------------------------------------------------

// Which of the six buttons the server would accept, so a refusal is never the way we find out.
export const availableActions = s => {
  const shipment = s || {}
  const paid = shipment.paid === true
  const shipsgo = shipment.shipsgo || {}
  const previous = shipsgo.previousShipmentIds || []
  return {
    retrack: paid && ['tracking', 'needs_attention'].includes(shipment.status),
    resolve:
      paid &&
      !['completed', 'closed', 'starting'].includes(shipment.status) &&
      (!!shipment.attention || shipment.status === 'needs_attention'),
    // The server allows close from every other status; matched deliberately, so an abandoned draft
    // can be closed with a note too.
    close: shipment.status !== 'closed',
    refund: paid,
    creditRefund: !!shipsgo.shipmentId || previous.length > 0,
    note: true
  }
}

export const validateRetrack = ({
  shipment,
  number,
  lineId,
  note,
  lines,
  acknowledged
} = {}) => {
  const errors = []
  const normalized = normalizeNumber(number)
  const kind = classifyNumber(normalized)

  if (kind === 'invalid') {
    errors.push('That is not a container number, bill of lading or booking number.')
  }
  if (kind === 'check_digit_failed') {
    errors.push(
      'That container number fails its check digit. Check each character against the document.'
    )
  }

  const line = (lines || []).find(row => row && row.id === lineId)
  if (!lineId || !line) {
    errors.push('Choose the shipping line.')
  } else {
    if (!line.active) {
      errors.push(
        'That line is switched off. Switch it on under Shipping lines first, or choose another.'
      )
    }
    if ((kind === 'container' || kind === 'reference') && !lineTracks(line, kind)) {
      const other = kind === 'container' ? KIND_LABELS.reference : KIND_LABELS.container
      errors.push(
        `${line.name} cannot track a ${KIND_LABELS[kind]} number. Ask the customer for the ${other}.`
      )
    }
  }

  if (!String(note || '').trim()) {
    errors.push('Say why you are re-tracking. It goes in the action log.')
  }

  const current = shipment || {}
  const unchanged = normalized === current.number && lineId === current.lineId
  // Re-tracking the same number and line is allowed only when the current attempt never produced a
  // ShipsGo shipment (a `start_failed`); otherwise the server answers 409 same_as_current.
  if (unchanged && (current.shipsgo || {}).shipmentId) {
    errors.push('Nothing has changed. Correct the number or the line.')
  }

  if (!acknowledged) {
    errors.push('Tick the box to confirm this spends a credit.')
  }

  return errors
}

// Credits are a warning, never a rule: a 0 last seen before somebody topped up would otherwise
// block the very re-track that recovers a start_failed.
export const retrackWarnings = ({ credits } = {}) =>
  credits === 0
    ? [
        'ShipsGo last reported 0 credits. Buy credits in ShipsGo first, or this re-track will fail to start.'
      ]
    : []

export const buildRetrackPayload = ({ number, lineId, note } = {}) => ({
  number: normalizeNumber(number),
  lineId,
  note: String(note || '').trim(),
  confirmCost: true
})

// One key per dialog opening (w-api §Admin endpoints). A v4 UUID, from the platform where it has
// one, because a retry that carries a new key is a second credit spent.
export const newIdempotencyKey = () => {
  const source = typeof crypto !== 'undefined' ? crypto : null
  if (source && typeof source.randomUUID === 'function') return source.randomUUID()
  const bytes = new Uint8Array(16)
  if (source && typeof source.getRandomValues === 'function') source.getRandomValues(bytes)
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`
}

// A retry of the SAME submission reuses its key; a corrected one gets a fresh key, because it is a
// different request and the server would otherwise replay the first answer.
export const keyForSubmission = (current, payload, makeKey = newIdempotencyKey) => {
  const signature = JSON.stringify(payload)
  if (current && current.signature === signature) return current
  // The dialog seeds a key when it opens with no signature yet; the first submission adopts it.
  if (current && current.key && current.signature == null) return { key: current.key, signature }
  return { key: makeKey(), signature }
}

export const recordedRefundTotal = actions =>
  (actions || []).reduce((total, action) => {
    if (!action || action.kind !== 'manual_refund') return total
    const amount = Number(action.amount)
    return Number.isNaN(amount) ? total : total + amount
  }, 0)

// The server's own figure first: it sums every refund, including the ones past the 200-row cap the
// detail's action list is truncated to.
export const refundedSoFar = shipment => {
  const total = ((shipment || {}).payment || {}).refundedTotal
  if (total !== null && total !== undefined && total !== '') {
    const number = Number(total)
    if (!Number.isNaN(number)) return number
  }
  return recordedRefundTotal((shipment || {}).actions)
}

export const refundableAmount = shipment => {
  const row = shipment || {}
  if (row.paid !== true) return 0
  const paid = Number((row.payment || {}).amount)
  if (Number.isNaN(paid)) return 0
  return Math.max(0, paid - refundedSoFar(row))
}

const decimalPlaces = value => {
  const parts = String(value).split('.')
  return parts.length > 1 ? parts[1].length : 0
}

export const validateRefund = ({ amount, paystackReference, note } = {}, maxAmount = 0) => {
  const errors = []
  const value = Number(amount)
  if (amount === null || amount === undefined || amount === '' || Number.isNaN(value) || value <= 0) {
    errors.push('Enter the amount refunded.')
  } else {
    if (decimalPlaces(amount) > 2) errors.push('Use at most two decimal places.')
    if (value > Number(maxAmount)) {
      errors.push(`That is more than the ${money(maxAmount)} still refundable.`)
    }
  }
  const reference = String(paystackReference || '').trim()
  if (!reference) errors.push('Enter the Paystack refund reference.')
  else if (reference.length > 100) errors.push('That reference is too long.')
  if (!String(note || '').trim()) errors.push('Add a note — who asked, and why.')
  return errors
}

export const buildRefundPayload = ({ amount, paystackReference, note } = {}, currency) => ({
  kind: 'manual_refund',
  amount: toMoneyString(amount),
  currency,
  paystackReference: String(paystackReference || '').trim(),
  note: String(note || '').trim()
})

// kind is 'note' or 'credit_refund_requested'.
export const buildNotePayload = (kind, note) => ({ kind, note: String(note || '').trim() })

export const buildResolvePayload = note => ({ note: String(note || '').trim() })

// The customer message is a separate field on purpose: the note is ours, the message is theirs.
export const buildClosePayload = (note, customerMessage) => {
  const payload = { note: String(note || '').trim() }
  const message = String(customerMessage || '').trim()
  if (message) payload.customerMessage = message
  return payload
}

// --- Shipping lines ---------------------------------------------------------------------------

export const normalizePrefixes = values => {
  const seen = []
  ;(values || []).forEach(value => {
    const prefix = String(value || '').trim().toUpperCase()
    if (prefix && !seen.includes(prefix)) seen.push(prefix)
  })
  return seen
}

export const validateLine = (form = {}) => {
  const errors = []
  const name = String(form.name || '').trim()
  if (!name) errors.push('Enter the line name.')
  else if (name.length > 80) errors.push('Keep the name under 80 characters.')

  const scac = String(form.scac || '').trim().toUpperCase()
  if (scac && !/^[A-Z0-9]{2,4}$/.test(scac)) {
    errors.push('A SCAC is two to four letters or digits.')
  }

  const code = String(form.shipsgoCarrierCode || '').trim().toUpperCase()
  if (!code) errors.push('Enter ShipsGo\'s carrier code for this line.')
  else if (!/^(SG_)?[A-Z0-9]{4}$/.test(code)) {
    errors.push(
      'ShipsGo codes are four letters or digits, optionally after SG_ (e.g. MSCU, SG_GRIM).'
    )
  }

  normalizePrefixes(form.prefixes).forEach(prefix => {
    if (!/^[A-Z]{3}[UJZ]$/.test(prefix)) {
      errors.push(`${prefix} is not a container prefix (three letters then U, J or Z).`)
    }
  })
  normalizePrefixes(form.referencePrefixes).forEach(prefix => {
    if (!/^[A-Z]{4}$/.test(prefix)) {
      errors.push(`${prefix} is not a reference prefix (four letters, e.g. MEDU).`)
    }
  })

  if (!form.tracksContainer && !form.tracksReference) {
    errors.push('A line must track containers, BL/booking numbers, or both.')
  }

  const position = Number(form.position)
  if (!Number.isInteger(position) || position < 0) {
    errors.push('Position is a whole number, 0 or more.')
  }

  return errors
}

// `country` is never sent: the server sets it, and it is not editable from this form.
export const buildLinePayload = (form = {}) => ({
  name: String(form.name || '').trim(),
  scac: String(form.scac || '').trim().toUpperCase(),
  shipsgoCarrierCode: String(form.shipsgoCarrierCode || '').trim().toUpperCase(),
  prefixes: normalizePrefixes(form.prefixes),
  referencePrefixes: normalizePrefixes(form.referencePrefixes),
  tracksContainer: !!form.tracksContainer,
  tracksReference: !!form.tracksReference,
  roro: !!form.roro,
  active: form.active === undefined ? true : !!form.active,
  position: Number(form.position)
})

// --- The carrier catalogue (spec: tracking-carrier-catalogue D1, D3) ---------------------------

// Where a row came from. `enriched` absent means curated: a server from before the catalogue only
// ever had rows a person set up.
export const lineSource = line => {
  const row = line || {}
  return row.enriched === false ? 'catalogue' : 'curated'
}

// Why an inactive line is off, and who did it. The two must not look alike: a row the nightly
// sync switched off comes back by itself when its code verifies again; a row a person switched off
// stays off whatever ShipsGo says. `null` for an active row.
export const lineOffReason = line => {
  const row = line || {}
  if (row.active !== false) return null
  if (row.retiredBySyncAt) {
    const status = String(row.shipsgoStatus || '').toUpperCase()
    return {
      by: 'sync',
      status: status || 'missing',
      at: row.retiredBySyncAt,
      label: `by the sync · ${status ? status : 'not in ShipsGo\'s list'} · ${formatDate(
        row.retiredBySyncAt
      )}`
    }
  }
  return { by: 'person', status: '', at: null, label: 'by a person' }
}

// Our name and ShipsGo's, compared the way a reader would: case and spacing ignored.
export const shipsgoNameDiffers = line => {
  const row = line || {}
  const squash = value => String(value || '').replace(/\s+/g, ' ').trim().toUpperCase()
  return !!row.shipsgoName && squash(row.shipsgoName) !== squash(row.name)
}

// What is wrong with a line that is switched on. An unverified line is the one that silently fails
// at ShipsGo, so it is worth a tag even though nothing is strictly broken yet.
export const lineProblems = line => {
  const row = line || {}
  if (!row.active) return []
  const problems = []
  if (!row.shipsgoCarrierCode) problems.push('No ShipsGo code')
  if (!row.tracksContainer && !row.tracksReference) problems.push('Tracks nothing')
  if (!row.verifiedAgainstShipsgoAt) problems.push('Not verified against ShipsGo')
  return problems
}

// --- Pricing and the ShipsGo account -----------------------------------------------------------

export const validatePrice = amount => {
  const errors = []
  const value = Number(amount)
  if (amount === null || amount === undefined || amount === '' || Number.isNaN(value) || value <= 0) {
    errors.push('Enter a price above zero.')
    return errors
  }
  if (decimalPlaces(amount) > 2) errors.push('Use at most two decimal places.')
  return errors
}

// The pricing endpoint never 404s: "no price" arrives as `amount: null` with `active: false`.
export const hasActivePrice = pricing =>
  !!pricing && pricing.amount != null && pricing.active === true && Number(pricing.amount) > 0

export const buildPricePayload = (amount, active) => ({
  amount: toMoneyString(amount),
  currency: 'GHS',
  active: !!active
})

// A SECONDARY hint only — the server's `misconfigured` flag is the authority on whether tracking
// actually works. This just catches a dashboard pointed at production while ShipsGo reports mock.
export const isProductionApi = (apiBase = process.env.REACT_APP_API_BASE) =>
  /\/\/api\.wabenzyautos\.com/i.test(apiBase || '')

export const accountProblems = (account, pricing, apiBase) => {
  const problems = []
  if (account) {
    if (account.misconfigured) {
      problems.push({
        level: 'error',
        key: 'misconfigured',
        message: `Tracking is switched off on this server: ${
          account.misconfiguredReason || 'ShipsGo is not set up for production.'
        } Customers cannot buy it and paid shipments cannot start until this is fixed and the server restarted.`
      })
    }
    if (!account.misconfigured && account.mode === 'mock' && isProductionApi(apiBase)) {
      problems.push({
        level: 'warning',
        key: 'mock_hint',
        message:
          'This dashboard points at the production API but ShipsGo reports MOCK mode. Check SHIPSGO_MODE on the server.'
      })
    }
    if (account.configured === false) {
      problems.push({
        level: 'error',
        key: 'not_configured',
        message:
          'ShipsGo has no API token on the server (SHIPSGO_API_TOKEN). Nothing can be tracked or sold.'
      })
    }
    if (account.webhookConfigured === false) {
      problems.push({
        level: 'warning',
        key: 'webhook_not_configured',
        message:
          'The ShipsGo webhook secret is not set (SHIPSGO_WEBHOOK_SECRET). Every ShipsGo update is refused; shipments only update on the scheduled sync.'
      })
    }
    // The server's flag, never a recomputation: the threshold is its setting to change.
    if (account.lowCredits === true) {
      problems.push({
        level: 'warning',
        key: 'low_credits',
        message: `ShipsGo credits are low: ${account.creditsRemaining} left (alert below ${account.lowCreditThreshold}). At 0, new shipments cannot start. Buy credits in ShipsGo.`
      })
    }
  }
  // The carrier catalogue's last run was refused or failed (spec: tracking-carrier-catalogue D4).
  // The server's sentence verbatim: it names the reason (too few carriers, a credit cost above
  // the ceiling, ShipsGo down) and nothing here could say it better.
  if (account && account.catalogueLastError) {
    problems.push({
      level: 'warning',
      key: 'catalogue_sync_failed',
      message: `The carrier catalogue did not update: ${account.catalogueLastError}. The line list is the one from the last good run.`
    })
  }
  if (pricing && !hasActivePrice(pricing)) {
    problems.push({
      level: 'warning',
      key: 'no_price',
      message:
        'No active price. Customers cannot buy Shipment tracking — the app says it is not available yet.' +
        (pricing.amount != null && pricing.active === false
          ? ' Selling is switched off; the price is kept.'
          : '')
    })
  }
  return problems
}

export const modeDisplay = account => {
  const row = account || {}
  if (row.misconfigured) {
    return { label: 'MISCONFIGURED', color: 'red', note: row.misconfiguredReason || '' }
  }
  if (row.mode === 'live') {
    return row.configured
      ? {
          label: 'LIVE',
          color: 'green',
          note: 'Real ShipsGo. Every new shipment spends a real credit.'
        }
      : // Never green without a token: a LIVE badge on a server that cannot call ShipsGo is worse
        // than no badge at all.
        {
          label: 'LIVE — NOT CONFIGURED',
          color: 'red',
          note: 'No API token on the server.'
        }
  }
  if (row.mode === 'mock') {
    return {
      label: 'MOCK',
      color: 'blue',
      note: 'Fake data, no credits spent — fine for local and staging.'
    }
  }
  return { label: row.mode || '', color: 'red', note: '' }
}

// --- The customer's view ----------------------------------------------------------------------
//
// `eta`, `route` and `vessel` are null before payment, while starting, while waiting, and `vessel`
// is null for every RoRo shipment (ShipsGo carries vessels on container movements). Every read here
// is guarded, because an unguarded one crashes the whole detail page.

export const journeySummary = view => {
  const state = view || {}
  const route = state.route || {}
  const from = route.from || null
  const to = route.to || null
  const etaAt = (state.eta || {}).at || null

  let routeText = null
  if (from && to) routeText = `From ${from.name} (${from.locode}) to ${to.name} (${to.locode})`
  else if (from) routeText = `From ${from.name} (${from.locode})`
  else if (to) routeText = `To ${to.name} (${to.locode})`

  const vessel = state.vessel || {}
  const vesselText = vessel.name
    ? vessel.name + (vessel.voyage ? ` / ${vessel.voyage}` : '')
    : null

  return {
    etaText: etaAt
      ? `Reaches ${(to && to.name) || 'the port'}: ${formatDate(etaAt)}`
      : 'No date from the line yet',
    etaEstimated: !!etaAt && (state.eta || {}).estimated === true,
    routeText,
    vesselText
  }
}

// --- Contacting the customer ------------------------------------------------------------------

// The admin row carries the contact as stored — Ghana local `0241234567` or international digits.
export const contactDigits = phone => {
  const digits = String(phone || '').replace(/\D/g, '')
  if (digits.length === 10 && digits.startsWith('0')) return `233${digits.slice(1)}`
  return digits
}

export const whatsappLink = (phone, text) => {
  const digits = contactDigits(phone)
  if (!digits) return ''
  return `https://wa.me/${digits}?text=${encodeURIComponent(text || '')}`
}

export const telLink = phone => {
  const digits = contactDigits(phone)
  return digits ? `tel:+${digits}` : ''
}

// The prefill, by open attention kind. These obey §15.6's copy rules: say "our team", never
// "human"; never "failed"; never promise a refund; never claim the shipment was found.
export const contactMessage = shipment => {
  const row = shipment || {}
  const number = row.number || ''
  const line = row.lineName || 'the shipping line'
  switch (row.attention) {
    case 'start_failed':
      return `Hello, this is Wabenzy Autos about your shipment tracking for ${number}. Your payment is safe — our team is setting up your tracking now and will update you shortly.`
    case 'not_found':
    case 'no_first_event':
      return `Hello, this is Wabenzy Autos about your shipment tracking for ${number}. We haven't received any updates from ${line} for this number yet. Could you send us a photo of your bill of lading so we can check the number and the shipping line?`
    case 'paused':
      return `Hello, this is Wabenzy Autos about your shipment ${number}. ${line} hasn't sent anything new for a while. Our team is checking and will update you.`
    default:
      return `Hello, this is Wabenzy Autos about your shipment tracking for ${number} (${line}).`
  }
}

// --- Errors -----------------------------------------------------------------------------------
//
// The server's own `detail` always wins — it is the sentence written for the person reading it, and
// it may be corrected without a dashboard release. These are only the fallback.

export const CODE_SENTENCES = {
  invalid_number: 'That is not a container number, bill of lading or booking number.',
  check_digit_failed: 'That container number fails its check digit.',
  line_required: 'Choose the shipping line.',
  line_unknown: 'That shipping line no longer exists.',
  line_unsupported_for_kind: 'That line cannot track this kind of number.',
  confirm_cost_required: 'The re-track was not confirmed, so no credit was spent.',
  idempotency_key_required:
    'The re-track was sent without its safety key, so nothing was done. Close the dialog and try again.',
  note_required: 'Add a note.',
  invalid_filter: 'One of the filters is not valid. Clear the filters and try again.',
  shipsgo_not_configured:
    'ShipsGo is not configured on the server yet, so tracking cannot be sold.',
  prefix_taken: 'One of those prefixes is already used by another active line.',
  not_retrackable: 'This shipment cannot be re-tracked in its current state.',
  not_resolvable: 'There is no open alert to resolve.',
  not_closable: 'This shipment is already closed.',
  same_as_current: 'That is the number and line already being tracked. Correct one of them.',
  refund_exceeds_paid: 'That would refund more than was paid.',
  duplicate_refund_reference: 'That Paystack reference is already recorded on this shipment.',
  line_in_use: 'Shipments use this line. Deactivate it instead of deleting it.'
}

// The house request helpers swallow the error and fire their own generic toast, so the server's
// sentence has to be dug out of the axios error they hand back.
export const isNoResponse = response =>
  !!(response && response.error) && !response.error.response

export const errorSentence = (response, fallback) => {
  const data =
    response && response.error && response.error.response
      ? response.error.response.data
      : null
  if (data && data.detail) return data.detail
  if (data && CODE_SENTENCES[data.code]) return CODE_SENTENCES[data.code]
  return isNoResponse(response) ? 'The server could not be reached.' : fallback
}
